import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../../../services/api';
import { updateProposalStage, fetchGoNoGoData, fetchRFPById, fetchMySections } from '../../services/rfpCoreService';

// Hooks
import { useRFPData } from './hooks/useRFPData';
import { useRFPUpdates } from './hooks/useRFPUpdates';
import { useContentEditor } from './hooks/useContentEditor';
import { useAIFeatures } from './hooks/useAIFeatures';
import { useVersionHistory } from './hooks/useVersionHistory';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';
import { useCollaboration, getUserColor, getUserInitials } from '../../team-member/editor/useCollaboration';

// Layout Components
import Sidebar from './components/Sidebar/Sidebar';
import ContentEditor from '../../team-member/editor/ContentEditor';
import KeyInformation from './components/KeyInformation/KeyInformation';
import GlobalSubmissionActions from './components/Footer/GlobalSubmissionActions';
import RFPAnalysisHeader from './components/RFPAnalysisHeader';
import InfoCardsRow from './components/InfoCardsRow';
import RFPOverlays from './components/RFPOverlays';
import TeamMemberDeadlineBar from './components/TeamMemberDeadlineBar';
import RFPModalsGroup from './components/RFPModalsGroup';
import SuccessToast from '../../../../shared/components/ui/SuccessToast';

// Utils & Constants
import { ArrowLeft } from 'lucide-react';
import GoNoGoResults from '../../shared/components/GoNoGoResults';
import '../../shared/RFPIntake.css';
import { getActiveStep, stripPrefix, formatForDateInput, formatCurrency } from './utils/helperUtils';
import { useScrollToTop } from '../../../../utils/scrollUtils';
import AnalysisSkeleton from './components/Skeletons/AnalysisSkeleton';
import './RFPAnalysisResult.css';
import '../../shared/components/VersionSidebar.css';


// How often (ms) view mode re-reads the open section so other people's saved edits show up.
const VIEW_REFRESH_INTERVAL_MS = 12000;

const RFPAnalysisResult = () => {
    const { rfpId, secId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { rfpData: stateData } = location.state || {};

    // ── UI State ───────────────────────────────────────────────────────────
    const [successToast, setSuccessToast] = useState(null);
    const [isJustApproved, setIsJustApproved] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [isKeyInfoModalOpen, setIsKeyInfoModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isDocInfoModalOpen, setIsDocInfoModalOpen] = useState(false);
    const [showAIModal, setShowAIModal] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [stageModal, setStageModal] = useState({ show: false, type: 'Accept', notes: '' });
    const [isMetadataReviewed, setIsMetadataReviewed] = useState(false);
    const [alertModal, setAlertModal] = useState({ show: false, message: '' });
    const [hoveredTabDetails, setHoveredTabDetails] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [showPPTModal, setShowPPTModal] = useState(false);
    const [isGeneratingSlides] = useState(false);

    // ── Extension & Decision State ────────────────────────────────────────
    const toDMY = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('T')[0].split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
        return dateStr;
    };

    const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
    const [extensionDate, setExtensionDate] = useState('');
    const [extensionNotes, setExtensionNotes] = useState('');
    const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
    const [decisionSection, setDecisionSection] = useState(null);
    const [decisionDate, setDecisionDate] = useState('');

    const handleExtensionClick = (section) => {
        setDecisionSection(section);
        setIsDecisionModalOpen(true);
        const requested = section.extension_request?.employee_requested_deadline;
        if (requested) setDecisionDate(toDMY(requested));
    };

    const handleGeneratePPT = async () => setShowPPTModal(true);

    // ── Document & Assignment State ────────────────────────────────────────
    const [documents, setDocuments] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [documentAssignments, setDocumentAssignments] = useState({});
    const blockEditorRef = useRef(null);

    // ── Role Detection ─────────────────────────────────────────────────────
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userDesignation = (user?.designation || '').toLowerCase();
    const userAuthRole = (user?.role || '').toLowerCase();
    const checkRole = (role) => {
        if (!role) return false;
        const r = String(role).toLowerCase();
        return r.includes('lead') || r.includes('manager') || r.includes('admin') || r.includes('reviewer');
    };
    const detectedIsManager = checkRole(userDesignation) || checkRole(userAuthRole);

    // ── Go/No-Go Decision States (Status 12 Flow) ──────────────────────────
    const [isGoNoGoPending, setIsGoNoGoPending] = useState(() => {
        const stateCode = location.state?.globalStatusCode || location.state?.analysisData?.global_status?.code;
        return Number(stateCode) === 12;
    });
    const [fetchingGoNoGo, setFetchingGoNoGo] = useState(false);
    const [goNoGoData, setGoNoGoData] = useState(null);
    const [goNoGoActiveTab, setGoNoGoActiveTab] = useState('client-info');
    const [goNoGoUpdatingDecision, setGoNoGoUpdatingDecision] = useState(false);
    const [goNoGoSelectedDecision, setGoNoGoSelectedDecision] = useState(null);
    const [goNoGoError, setGoNoGoError] = useState(null);

    const {
        editableData,
        setEditableData,
        teamMembers,
        assignedUsers,
        fetchTeamMembers,
        isInitialLoading,
        sidebarSections,
        fetchRFPData,
        fetchOverview,
        fetchAssignedUsersData,
        effectiveIsManager,
        accessError
    } = useRFPData(rfpId, stateData, location.state, detectedIsManager, setAssignments);

    const isManager = effectiveIsManager !== undefined ? effectiveIsManager : detectedIsManager;
    const isTeamMember = !isManager;

    const selectedSection = useMemo(() => {
        if (!sidebarSections || sidebarSections.length === 0) return null;
        if (secId) return sidebarSections.find(s => s.id === secId || String(s.id) === String(secId)) || sidebarSections[0];
        return sidebarSections[0];
    }, [secId, sidebarSections]);

    useEffect(() => {
        if (selectedSection) {
            const nextKey = selectedSection.label || selectedSection.key;
            if (nextKey !== activeSection) setActiveSection(nextKey);
        }
    }, [selectedSection, activeSection]);

    useEffect(() => {
        if (editableData?.global_status?.code === 12) {
            setIsGoNoGoPending(true);
        }
    }, [editableData]);

    useEffect(() => {
        if (isGoNoGoPending && rfpId) {
            const fetchGoNoGoDetails = async () => {
                setFetchingGoNoGo(true);
                setGoNoGoError(null);
                try {
                    const res = await fetchGoNoGoData(rfpId);
                    setGoNoGoData(res.data);
                } catch (err) {
                    console.error('Error fetching go-no-go:', err);
                    setGoNoGoError('Failed to load Go/No-Go assessment data.');
                } finally {
                    setFetchingGoNoGo(false);
                }
            };
            fetchGoNoGoDetails();
        }
    }, [isGoNoGoPending, rfpId]);

    // Unpack WebSocket real-time updates
    const { lastMessage } = useRealTimeChat();

    // Listen for WebSocket events to synchronize the active proposal workspace in real-time
    useEffect(() => {
        if (!lastMessage) return;

        const { type, payload } = lastMessage;

        const WORKSPACE_SYNC_EVENTS = [
            'SECTION_SUBMITTED',
            'SECTION_UPDATED',
            'SECTION_RETURNED',
            'SECTION_APPROVED',
            'SECTION_REASSIGNED',
            'RFP_ASSIGNED',
            'NOTIFICATION_RECEIVED',
            'NOTIFICATION'
        ];

        const resolvedType = type || lastMessage.event;

        if (WORKSPACE_SYNC_EVENTS.includes(resolvedType)) {
            const eventRfpId = payload?.rfpId || payload?.rfp_id || payload?.data?.rfpId || payload?.data?.rfp_id || payload?.notification?.rfp_id;

            // Only refetch if the event affects the currently active proposal
            if (eventRfpId && String(eventRfpId) === String(rfpId)) {
                console.log(`✅ [WS REFRESH] Synchronizing active workspace (${rfpId}) due to: ${resolvedType}`);
                if (typeof fetchRFPData === 'function') fetchRFPData(true);
                if (typeof fetchOverview === 'function') fetchOverview();
                if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();
            }
        }
    }, [lastMessage, rfpId, fetchRFPData, fetchOverview, fetchAssignedUsersData]);

    const handleGoNoGoSubmit = (stageCode) => {
        setAlertModal({
            show: true,
            message: stageCode === 8
                ? "Are you sure you want to move the proposal forward for further preparation and submission activities?"
                : "Are you sure you want to discontinue further proposal activities?",
            stageCode: stageCode,
            isConfirmation: true
        });
    };

    const handleConfirmGoNoGo = async () => {
        const { stageCode } = alertModal;
        setGoNoGoSelectedDecision(stageCode);
        setGoNoGoUpdatingDecision(true);
        try {
            await updateProposalStage(rfpId, stageCode, stageCode, "");
            setAlertModal({ show: false, message: '', stageCode: null });
            if (stageCode === 8) {
                setIsGoNoGoPending(false);
                if (location.state) {
                    location.state.globalStatusCode = null;
                    if (location.state.analysisData) {
                        location.state.analysisData.global_status.code = 8;
                    }
                }
                await fetchRFPData();
            } else {
                navigate('/proposals');
            }
        } catch (err) {
            console.error('Error submitting Go/No-Go decision:', err);
            setAlertModal({ show: true, message: "Failed to update Go/No-Go decision." });
        } finally {
            setGoNoGoUpdatingDecision(false);
            setGoNoGoSelectedDecision(null);
        }
    };

    const sectionKeys = useMemo(() => sidebarSections.map(s => s.label || s.key), [sidebarSections]);

    const { showVersionHistory, setShowVersionHistory, versionsList, activeVersionMenuId, setActiveVersionMenuId, isFetchingVersionDetails, fetchVersions, handleViewVersion, fetchComparison, comparisonData, showComparisonModal, setShowComparisonModal, isSelectingCompareFromView, setIsSelectingCompareFromView, compareSourceVersion, setCompareSourceVersion, isFetchingComparison, showLabelModal, setShowLabelModal, setLabelTargetVersion, versionLabelText, setVersionLabelText, handleRenameVersion, showRevertModal, setShowRevertModal, revertTargetVersion, setRevertTargetVersion, revertReason, setRevertReason, handleRevertVersion, showVersionDetailsModal, setShowVersionDetailsModal, selectedVersionData, setSelectedVersionData, handleApproveVersion, isApprovingVersion, approvingVersionNum } = useVersionHistory(rfpId, activeSection, setEditableData, editableData);

    // Refs so callbacks (defined after the hook) can be passed as stable closures
    const onSaveSuccessRef = useRef(null);

    const { isUpdating, isStageUpdating, updateStatus, setUpdateStatus, stageError, setStageError, executeStageTransition, handleUpdate, handleToggleSectionApproval, handleSubmitToLead, handleClarificationRequest, handleUpdateMetadata, handleRequestExtension, handleExtensionDecision, isSubmittingToLead, isApprovingSection } = useRFPUpdates(
        rfpId, editableData, setEditableData, setActiveSection, setAlertModal, isManager,
        activeSection, fetchRFPData, fetchOverview, setAssignments, fetchVersions,
        (saveInfo) => { if (onSaveSuccessRef.current) onSaveSuccessRef.current(saveInfo); }
    );

    const handleApproveSectionVersionWithReload = useCallback(async (versionNumber) => {
        return handleApproveVersion(versionNumber, async (verNum, data) => {
            setUpdateStatus({ type: 'success', message: data?.message || `Version ${verNum} approved as main document.` });
            if (fetchRFPData) await fetchRFPData(true);
            if (fetchOverview) await fetchOverview(setAssignments);
        });
    }, [handleApproveVersion, fetchRFPData, fetchOverview, setAssignments, setUpdateStatus]);


    // Extension approval & Section reassignment toast — runs AFTER useRFPUpdates so setUpdateStatus is defined
    useEffect(() => {
        if (!lastMessage) return;
        const { type, payload } = lastMessage;

        // Match standard notification type or generic notification wrappers
        const isNotification = type === 'NOTIFICATION_RECEIVED' || type === 'NOTIFICATION' || String(lastMessage.event).toUpperCase() === 'NOTIFICATION';
        const isAssignment = type === 'RFP_ASSIGNED' || type === 'SECTION_REASSIGNED' || String(lastMessage.event).toUpperCase() === 'RFP_ASSIGNED' || String(lastMessage.event).toUpperCase() === 'SECTION_REASSIGNED';

        if (isNotification || isAssignment) {
            const eventRfpId = payload?.rfpId || payload?.rfp_id || payload?.data?.rfpId || payload?.data?.rfp_id || payload?.notification?.rfp_id;

            if (eventRfpId && String(eventRfpId) === String(rfpId)) {
                const msg = (
                    payload?.message ||
                    payload?.title ||
                    payload?.data?.message ||
                    payload?.data?.title ||
                    payload?.notification?.message ||
                    ''
                ).toLowerCase();

                if (msg.includes('extension') && msg.includes('approved')) {
                    const newDeadline = payload?.deadline || payload?.data?.deadline || payload?.notification?.deadline || payload?.data?.new_deadline;

                    console.log('🎉 [WS] Extension Approved notification received! Triggering UI state shifts.');

                    // 1. Refresh section details automatically
                    if (typeof fetchRFPData === 'function') fetchRFPData(true);
                    if (typeof fetchOverview === 'function') fetchOverview();
                    if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();

                    // 2. Display success banner alert notification
                    setSuccessToast({
                        message: '✓ Extension Approved',
                        rfpName: `Your section deadline has been updated to ${newDeadline ? toDMY(newDeadline) : '15-06-2026'}.`
                    });
                    setTimeout(() => setSuccessToast(null), 5000);

                    // 3. Trigger visual success glow/pulse highlight inside deadline card
                    setIsJustApproved(true);
                    setTimeout(() => setIsJustApproved(false), 5000);
                } else if (isAssignment || msg.includes('assign') || msg.includes('reassign')) {
                    console.log('🎉 [WS] Section Reassignment notification received! Triggering UI state shifts.');

                    // 1. Refresh section details automatically
                    if (typeof fetchRFPData === 'function') fetchRFPData(true);
                    if (typeof fetchOverview === 'function') fetchOverview();
                    if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();

                    // 2. Display success toast notification
                    let detailsText = 'Section assignments have been updated.';
                    const rawMsgObj = payload?.message || payload?.data?.message || payload?.notification?.message || payload;
                    if (rawMsgObj && typeof rawMsgObj === 'object') {
                        const sec = rawMsgObj.section || rawMsgObj.section_title || rawMsgObj.title || '';
                        const usr = rawMsgObj.assigned_to || rawMsgObj.assigned_user || '';
                        if (sec && usr) {
                            detailsText = `Section: ${sec} assigned to ${usr}`;
                        } else if (rawMsgObj.body || rawMsgObj.content || rawMsgObj.text) {
                            detailsText = rawMsgObj.body || rawMsgObj.content || rawMsgObj.text;
                        }
                    } else if (typeof rawMsgObj === 'string') {
                        detailsText = rawMsgObj;
                    }

                    // Strip any leading checkmarks from message
                    const cleanMsg = detailsText.replace(/^✓\s*/, '');

                    setSuccessToast({
                        message: '✓ Section Reassigned Successfully',
                        rfpName: cleanMsg
                    });
                    setTimeout(() => setSuccessToast(null), 5000);
                }
            }
        }
    }, [lastMessage, rfpId, fetchRFPData, fetchOverview, fetchAssignedUsersData]);

    const {
        selectedCell, setSelectedCell, localBlocks, setLocalBlocks,
        isEditingContent, setIsEditingContent,
        handleBlockEdit, handleTableCellEdit, handleSidePanelEdit,
        handleTableAction, handleRenameColumn, cancelEditing,
        addToHistory, undo, redo, startEditing,
        applyRemoteContent,
    } = useContentEditor(activeSection, editableData, setEditableData);

    // ── Fetch latest saved content for a specific section from the backend ──────────
    const fetchLatestSectionContent = useCallback(async (sectionKey) => {
        if (!rfpId || !sectionKey) return null;
        try {
            const response = isManager ? await fetchRFPById(rfpId) : await fetchMySections(rfpId);
            const freshData = response.data;
            if (freshData && freshData[sectionKey] !== undefined) return freshData[sectionKey];
            const secs = freshData?.sections || [];
            const sec = secs.find(s => s.label === sectionKey || s.key === sectionKey || s.section_title === sectionKey);
            // Read mode shows the APPROVED content for everyone, so only `content` is returned here
            // (never `draft_content`).
            if (sec && sec.content !== undefined) return sec.content;
            return null;
        } catch (err) {
            console.warn('[fetchLatestSectionContent] Failed:', err);
            return null;
        }
    }, [rfpId, isManager]);

    // Show new APPROVED content in read mode: updates the section object (what read mode reads)
    // and the editableData copy used by the compare view.
    const applyApprovedContent = useCallback((sectionKey, fresh) => {
        applyRemoteContent(sectionKey, fresh);
        setEditableData(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                sections: (prev.sections || []).map(s =>
                    (s.label === sectionKey || s.key === sectionKey || s.section_title === sectionKey)
                        ? { ...s, content: fresh }
                        : s
                ),
            };
        });
    }, [applyRemoteContent, setEditableData]);
    // ── Section save success: toast for the saving user, update ref ─────────────────
    const handleSectionSaveSuccess = useCallback(({ sectionKey, savedByName }) => {
        // No-op to avoid duplicate toast for the saving user (backend response toast is shown instead)
    }, []);

    // Keep the ref current so the closure in useRFPUpdates always has the latest
    useEffect(() => { onSaveSuccessRef.current = handleSectionSaveSuccess; }, [handleSectionSaveSuccess]);

    // ── SECTION_CONTENT_SAVED / SECTION_UPDATED: sync peers ────────────────────────
    // This effect handles the incoming WS event emitted by the backend AFTER any user saves.
    // It decides whether to: auto-refresh, show a conflict prompt, or silently update.
    useEffect(() => {
        if (!lastMessage) return;
        const resolvedType = lastMessage.type || lastMessage.event || '';
        const isSectionSaved = [
            'SECTION_CONTENT_SAVED', 'SECTION_UPDATED', 'SECTION_SUBMITTED'
        ].includes(resolvedType.toUpperCase());
        if (!isSectionSaved) return;

        const payload = lastMessage.payload || lastMessage.data || lastMessage;
        const eventRfpId = payload?.rfp_id || payload?.rfpId;
        const eventSectionKey = payload?.section_key || payload?.sectionKey || payload?.section_id;

        // Only apply to the currently open RFP
        if (eventRfpId && String(eventRfpId) !== String(rfpId)) return;

        // Only apply if the event is about the currently active section. The backend may
        // identify the section by label/key OR by id, so accept any of them.
        const activeIds = [
            activeSection, collabSectionId,
            currentActiveSectionObj?.id, currentActiveSectionObj?.section_id, currentActiveSectionObj?._id,
            currentActiveSectionObj?.key, currentActiveSectionObj?.label,
        ].filter(Boolean).map(String);
        const isCurrentSection = !eventSectionKey || activeIds.includes(String(eventSectionKey));

        if (!isCurrentSection) {
            // Another section was updated — just silently refresh full data
            if (typeof fetchRFPData === 'function') fetchRFPData(true);
            return;
        }

        // While editing, the live shared draft keeps everyone in sync. Saves by others only change
        // the draft (and version history), never the approved content, so there is nothing to apply.
        if (isEditingContent) return;

        // Read mode: show the approved content only if it actually changed (e.g. a version was approved).
        fetchLatestSectionContent(activeSection).then(freshContent => {
            if (freshContent === null || freshContent === undefined) return;
            const shownApproved = currentActiveSectionObj?.content ?? currentActiveSectionObj?.text ?? '';
            const asKey = (v) => (typeof v === 'string' ? v : JSON.stringify(v ?? ''));
            if (asKey(freshContent) === asKey(shownApproved)) return;
            applyApprovedContent(activeSection, freshContent);
            setSuccessToast({
                message: `✓ Section updated`,
                rfpName: `New approved content is now showing.`
            });
            setTimeout(() => setSuccessToast(null), 4000);
        });    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    // ── SECTION_CONTENT_REVERTED: global force-refresh for all peers ─────────────
    // Fires when the Team Lead issues a revert. ALL users on the same rfp+section
    // MUST immediately discard local drafts and display the restored baseline content.
    useEffect(() => {
        if (!lastMessage) return;
        const resolvedType = String(lastMessage.type || lastMessage.event || '').toUpperCase();
        if (resolvedType !== 'SECTION_CONTENT_REVERTED') return;

        const payload = lastMessage.payload || lastMessage.data || lastMessage;
        const eventRfpId = payload?.rfp_id || payload?.rfpId;
        const eventSectionKey = payload?.section_key || payload?.sectionKey || payload?.section_id;
        const revertedBy = payload?.reverted_by || payload?.revertedBy || 'Team Lead';
        const restoredContent = payload?.content ?? null;

        // Only act on our own RFP
        if (eventRfpId && String(eventRfpId) !== String(rfpId)) return;

        const isCurrentSection = !eventSectionKey || eventSectionKey === activeSection;

        const applyRevert = async () => {
            if (restoredContent !== null) {
                // Apply the content shipped with the WS event (no extra HTTP round-trip needed)
                applyRemoteContent(activeSection, restoredContent);
            } else {
                // Fallback: re-fetch from backend
                const freshContent = await fetchLatestSectionContent(activeSection);
                if (freshContent !== null) applyRemoteContent(activeSection, freshContent);
            }
            // Force-exit edit mode so the user sees the reverted content.
            // Drop the edit session first so the reverted content is not overwritten by a leave-save.
            editSessionRef.current = null;
            cancelEditing();
            setSuccessToast({
                message: `↩ Content reverted by ${revertedBy}`,
                rfpName: 'Section content was restored to the original baseline by the Team Lead.'
            });
            setTimeout(() => setSuccessToast(null), 6000);
        };

        if (isCurrentSection) {
            applyRevert();
        } else {
            // Another section was reverted — just silently refresh so sidebar status syncs
            if (typeof fetchRFPData === 'function') fetchRFPData(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    // ── Leaving edit mode (Cancel) ───────────────────────────────────────────────────
    // Cancel just closes the editor – NO API call. The shared draft lives on the server: it is
    // saved when the last person leaves, every 5 minutes while people work, and on Save/Submit.
    // Read mode goes back to the APPROVED content (cancelEditing restores it).
    const editSessionRef = useRef(null);            // { dirty } – did THIS user change anything
    const collabStatusRef = useRef('unavailable');  // latest live-connection status

    const leaveEditing = useCallback(async () => {
        const session = editSessionRef.current;
        // Only when live sync is not working can edits be lost by leaving: warn in that case.
        if (session?.dirty && ['unavailable', 'error', 'disconnected'].includes(collabStatusRef.current)) {
            const ok = window.confirm(
                'Live collaboration is not connected, so your latest changes may not be saved. Leave anyway?'
            );
            if (!ok) return;
        }
        editSessionRef.current = null;
        cancelEditing();
    }, [cancelEditing]);
    const handleCancelEditing = leaveEditing;
    const handleCancelSync = leaveEditing;

    const isNoGo = editableData?.global_status?.code === 9 ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO_GO' ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO-GO' ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO GO';

    const isTeamLeadLockedStatus = (globalStatus) => [5, 6, 7].includes(Number(globalStatus));
    const isTeamLeadViewOnly = isManager && isTeamLeadLockedStatus(editableData?.global_status?.code);

    const getTeamLeadViewOnlyMessage = () => {
        const status = Number(editableData?.global_status?.code);
        if (status === 5) return "Editing is disabled because this proposal has been submitted to the client.";
        if (status === 6) return "Editing is disabled because this proposal has been accepted (WON).";
        if (status === 7) return "Editing is disabled because this proposal has been rejected (LOST).";
        return "Editing is disabled because this proposal is already finalized/submitted.";
    };

    // ── Collaboration setup (additive – no effect if WS server is unavailable) ──
    const currentActiveSectionObj = useMemo(() => {
        const sectionsList = editableData?.sections || sidebarSections || [];
        if (!sectionsList.length) return null;
        return sectionsList.find(s =>
            (s.label && s.label === activeSection) ||
            (s.key && s.key === activeSection) ||
            (s.section_title && s.section_title === activeSection) ||
            (s.section_key && s.section_key === activeSection) ||
            (s.id && String(s.id) === String(activeSection)) ||
            (s.section_id && String(s.section_id) === String(activeSection))
        ) || null;
    }, [editableData?.sections, sidebarSections, activeSection]);

    // The section the live room belongs to. `selectedSection` is chosen by ID from the URL, so when it
    // is the section on screen it is the most reliable source; otherwise fall back to a label lookup.
    const selectedIsActive = Boolean(selectedSection) &&
        ((selectedSection.label || selectedSection.key) === activeSection);
    const collabSectionObj = selectedIsActive ? selectedSection : currentActiveSectionObj;
    const collabSectionId = collabSectionObj?.section_id ||
        collabSectionObj?.id ||
        collabSectionObj?._id ||
        null;

    // Lock the live room to the section where Edit was clicked. Its ID is captured ONCE, at click
    // time, and is never recomputed while editing, so the connection can't drift to another section.
    const [editingSection, setEditingSection] = useState(null); // { key, id }
    const startEditingSection = useCallback((...args) => {
        console.log('[Collab] Edit started', {
            section: activeSection,
            roomId: collabSectionId,
            ids: { id: collabSectionObj?.id, section_id: collabSectionObj?.section_id, _id: collabSectionObj?._id },
        });
        setEditingSection({ key: activeSection, id: collabSectionId });
        return startEditing(...args);
    }, [startEditing, activeSection, collabSectionId, collabSectionObj]);

    // Read-only section detection: Expired (56), Submitted (53), Approved (54), Auto-Closed (57)
    const activeSectionObj = currentActiveSectionObj || selectedSection;

    const { isRefining, aiPrompt, setAiPrompt, handleAIImprove } = useAIFeatures(
        rfpId,
        editableData,
        activeSection,
        selectedCell,
        handleSidePanelEdit,
        setUpdateStatus,
        fetchRFPData,
        setEditableData,
        activeSectionObj
    );
    const activeSectionStatusCode = Number(
        activeSectionObj?.status?.code ||
        activeSectionObj?.status_code ||
        editableData?.SECTION_STATUS_CODE?.[activeSection] ||
        0
    );
    const activeSectionStatusStr = String(
        activeSectionObj?.status?.label ||
        activeSectionObj?.status ||
        editableData?.SECTION_STATUS?.[activeSection] ||
        ''
    ).toLowerCase();

    const isSectionReadOnly = [53, 54, 56, 57].includes(activeSectionStatusCode) ||
        activeSectionStatusStr.includes('submitted') ||
        activeSectionStatusStr.includes('approved') ||
        activeSectionStatusStr.includes('expired') ||
        activeSectionStatusStr.includes('auto-closed') ||
        activeSectionStatusStr.includes('closed') ||
        isTeamLeadViewOnly ||
        isNoGo;

    // No public fallback: RFP content must never be sent to a third-party demo server.
    // If neither env var is set, collaboration is disabled and standard editing is used.
    const wsBaseUrl = process.env.REACT_APP_COLLAB_WS_URL || (process.env.REACT_APP_BASE_URL ? process.env.REACT_APP_BASE_URL.replace(/^http/i, 'ws') + '/ws/collab' : '');
    const collabUserMeta = useMemo(() => {
        const getCleanName = () => {
            const rawName = user?.employee_name || user?.full_name || user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
            if (rawName) return rawName;

            const email = user?.email || 'Collaborator';
            if (email.includes('@')) {
                const part = email.split('@')[0];
                return part
                    .split(/[._-]/)
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
            return email;
        };

        const finalName = getCleanName();
        return {
            id: user?.id || user?.email || '',
            name: finalName,
            color: getUserColor(String(user?.id || user?.email || '')),
            initials: getUserInitials(finalName),
        };
    }, [user?.id, user?.email, user?.name, user?.employee_name, user?.full_name, user?.first_name, user?.last_name]);

    const { ydoc: collabYdoc, provider: collabProvider, status: collabStatus, collaborators, collabReady, retry: collabRetry } =
        useCollaboration({
            rfpId,
            sectionId: editingSection?.id,
            user: collabUserMeta,
            wsBaseUrl,
            isEditing: isEditingContent,
            // Off the moment the user moves to another section (no brief join to the new section's room).
            enabled: !isSectionReadOnly && isEditingContent && Boolean(editingSection) && editingSection.key === activeSection,
        });

    // ── Edit session tracking ───────────────────────────────────────────────────────
    // `dirty` is set by the editor only for THIS user's own edits (never peers' edits).
    collabStatusRef.current = collabStatus;
    useEffect(() => {
        if (isEditingContent) {
            if (!editSessionRef.current) editSessionRef.current = { dirty: false };
        } else {
            editSessionRef.current = null;
        }
    }, [isEditingContent]);

    const markLocalEdit = useCallback(() => {
        if (editSessionRef.current) editSessionRef.current.dirty = true;
    }, []);

    // Approved content currently shown in read mode for the open section (used to detect real changes).
    const approvedShownRef = useRef('');
    approvedShownRef.current = currentActiveSectionObj?.content ?? currentActiveSectionObj?.text ?? '';

    // Close code 4409: the section was submitted / approved / locked. The editor is read-only and
    // does not reconnect (handled in useCollaboration). Re-read the data so statuses are current.
    useEffect(() => {
        if (collabStatus === 'locked') {
            if (typeof fetchRFPData === 'function') fetchRFPData(true);
            setSuccessToast({
                message: '🔒 This section is now locked',
                rfpName: 'It was submitted or approved, so it is read-only.'
            });
            setTimeout(() => setSuccessToast(null), 6000);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collabStatus]);
    // ── Keep VIEW mode in sync with what collaborators save ──────────────────────────
    // After you leave the editor your screen shows the content you last saw. Others may keep
    // editing and save later (their leave-save, or the backend's periodic / last-user-leaves
    // save), and nothing pushes that to you. So while you are NOT editing, re-read the
    // section from the server on a timer, when the tab regains focus, and right after
    // you leave, and show it if it changed.
    const lastPolledRef = useRef({ key: null, value: null });
    useEffect(() => {
        if (isEditingContent || !rfpId || !activeSection || isInitialLoading) return;
        let cancelled = false;
        const sectionKey = activeSection;
        const toKey = (v) => (typeof v === 'string' ? v : JSON.stringify(v ?? ''));

        const refresh = async () => {
            if (cancelled || document.visibilityState !== 'visible') return;
            const fresh = await fetchLatestSectionContent(sectionKey);
            // Re-check after the await: the user may have started editing / saving meanwhile.
            if (cancelled || fresh === null || fresh === undefined) return;

            const freshKey = toKey(fresh);
            const sameSectionAsLastPoll = lastPolledRef.current.key === sectionKey;
            const unchangedOnServer = sameSectionAsLastPoll && lastPolledRef.current.value === freshKey;
            lastPolledRef.current = { key: sectionKey, value: freshKey };

            // Apply when the server copy changed since we last looked, or when it differs from
            // what is on screen the first time we look at this section.
            const shown = toKey(approvedShownRef.current);
            if (!unchangedOnServer && freshKey !== shown) {
                applyApprovedContent(sectionKey, fresh);
            }
        };

        const first = setTimeout(refresh, 1500);                 // soon after leaving the editor
        const interval = setInterval(refresh, VIEW_REFRESH_INTERVAL_MS);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            cancelled = true;
            clearTimeout(first);
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditingContent, isInitialLoading, rfpId, activeSection, fetchLatestSectionContent, applyApprovedContent]);

    // ── When this user leaves the collaborative editor the backend may write a version
    //    (last user leaving). Refresh the list shortly after so it shows up. ──────────
    const wasEditingRef = useRef(false);
    useEffect(() => {
        if (wasEditingRef.current && !isEditingContent) {
            const t1 = setTimeout(() => fetchVersions(true), 2000);
            const t2 = setTimeout(() => fetchVersions(true), 7000);
            wasEditingRef.current = isEditingContent;
            return () => { clearTimeout(t1); clearTimeout(t2); };
        }
        wasEditingRef.current = isEditingContent;
    }, [isEditingContent, fetchVersions]);

    // ── Refetch my-sections on section switch so status & "Updated at" are current ────
    const prevSectionIdRef = useRef(collabSectionId);
    useEffect(() => {
        if (prevSectionIdRef.current && collabSectionId && prevSectionIdRef.current !== collabSectionId) {
            console.log(`🔄 [Section Switch] Changed from ${prevSectionIdRef.current} to ${collabSectionId}. Refetching fresh sections...`);
            fetchRFPData(true);
        }
        prevSectionIdRef.current = collabSectionId;
    }, [collabSectionId, fetchRFPData]);

    // ── Effects ────────────────────────────────────────────────────────────
    useScrollToTop([activeSection, isGoNoGoPending, goNoGoActiveTab]);
    useEffect(() => {
        // Switching sections closes the editor (the old section's connection is torn down).
        setIsEditingContent(false);
        setUpdateStatus(null);
    }, [activeSection, setIsEditingContent, setUpdateStatus]);
    useEffect(() => { if (isManager) fetchTeamMembers(); }, [isManager, fetchTeamMembers]);

    const activeStep = getActiveStep(editableData, isManager);

    const allSectionsApproved = useMemo(() => {
        if (!sectionKeys.length) return false;
        return sectionKeys.every(k => (editableData?.SECTION_STATUS?.[k] === 'Approved' || editableData?.section_status?.[k] === 'Approved'));
    }, [sectionKeys, editableData?.SECTION_STATUS, editableData?.section_status]);

    useEffect(() => {
        if (editableData?.SECTION_ASSIGNMENTS) setAssignments(prev => ({ ...prev, ...editableData.SECTION_ASSIGNMENTS }));
    }, [editableData?.SECTION_ASSIGNMENTS]);

    const handleMetadataChange = (key, value) => setEditableData(prev => ({ ...prev, [key]: value, [key.toLowerCase()]: value }));

    const getViewOnlyBannerStyle = () => {
        const status = Number(editableData?.global_status?.code);
        if (status === 6) { // WON
            return {
                backgroundColor: '#ECFDF5',
                border: '1px solid #A7F3D0',
                color: '#047857'
            };
        }
        if (status === 7) { // LOST
            return {
                backgroundColor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                color: '#B91C1C'
            };
        }
        // Default / Status 5 (Submitted)
        return {
            backgroundColor: '#FFF1EC',
            border: '1px solid #FED7AA',
            color: '#E9471D'
        };
    };

    const safeExecuteStageTransition = (...args) => { if (isTeamLeadViewOnly) return; return executeStageTransition(...args); };
    const safeHandleUpdate = (...args) => { if (isTeamLeadViewOnly) return; return handleUpdate(...args); };
    const safeHandleToggleSectionApproval = (...args) => { if (isTeamLeadViewOnly) return; return handleToggleSectionApproval(...args); };
    // Submit keeps the live connection open until the call finishes (the server snapshots the shared
    // draft and then locks the section, closing everyone's connection with code 4409).
    const safeHandleSubmitToLead = async (...args) => {
        if (isTeamLeadViewOnly) return;
        try {
            return await handleSubmitToLead(...args);
        } finally {
            setIsEditingContent(false);
        }
    };
    const safeHandleUpdateMetadata = (...args) => { if (isTeamLeadViewOnly) return; return handleUpdateMetadata(...args); };
    const safeHandleRequestExtension = (...args) => { if (isTeamLeadViewOnly) return; return handleRequestExtension(...args); };
    const safeHandleExtensionDecision = (...args) => { if (isTeamLeadViewOnly) return; return handleExtensionDecision(...args); };
    const safeHandleAIImprove = (...args) => { if (isTeamLeadViewOnly) return; return handleAIImprove(...args); };

    const isAnyModalOpen = !!(
        showExport ||
        showPPTModal ||
        showVersionDetailsModal ||
        showComparisonModal ||
        isKeyInfoModalOpen ||
        isAssignModalOpen ||
        isDocInfoModalOpen ||
        stageModal?.show ||
        showAIModal ||
        showLabelModal ||
        showRevertModal ||
        isDecisionModalOpen ||
        isExtensionModalOpen ||
        alertModal?.show ||
        isGeneratingSlides
    );

    // ── Go/No-Go Decision Screen (Pending 12) ──────────────────────────────
    if (isGoNoGoPending) {
        return (
            <div className="rfp-intake-page" style={{ padding: '2rem', minHeight: '100vh', background: '#F8F9FA' }}>
                <div className="intake-header-refined" style={{ marginBottom: '2rem' }}>
                    <button className="refined-back-btn" onClick={() => navigate('/proposals')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div className="refined-header-text">
                        <h1>Proposal Evaluation</h1>
                        <p>Review the AI-generated assessment insights and determine whether to proceed with the proposal opportunity.</p>
                    </div>
                </div>

                <div className="scanning-container animate-fade-in" style={{ maxWidth: '1000px', margin: '0 auto', background: '#FFFFFF', borderRadius: '6px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '0.2rem' }}>
                    {fetchingGoNoGo ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6rem 0', gap: '1.25rem' }}>
                            <div className="animate-spin" style={{ width: '40px', height: '40px', border: '4px solid #E2E8F0', borderTopColor: '#FF5526', borderRadius: '50%' }}></div>
                            <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>Fetching Go/No-Go Assessment...</span>
                        </div>
                    ) : goNoGoError ? (
                        <div style={{ textAlign: 'center', padding: '4rem 0', color: '#EF4444' }}>
                            <p style={{ fontWeight: '600', fontSize: '16px' }}>{goNoGoError}</p>
                            <button onClick={() => window.location.reload()} style={{ marginTop: '1.25rem', padding: '8px 16px', background: '#FF5526', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Retry</button>
                        </div>
                    ) : goNoGoData ? (
                        <GoNoGoResults
                            goNoGoData={goNoGoData}
                            activeTab={goNoGoActiveTab}
                            setActiveTab={setGoNoGoActiveTab}
                            handleGoNoGoSubmit={handleGoNoGoSubmit}
                            updatingDecision={goNoGoUpdatingDecision}
                            selectedDecision={goNoGoSelectedDecision}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '4rem 0', color: '#64748B' }}>
                            No assessment data available.
                        </div>
                    )}
                </div>

                <RFPOverlays
                    updateStatus={updateStatus} alertModal={alertModal} setAlertModal={setAlertModal}
                    onConfirm={handleConfirmGoNoGo}
                    navigate={navigate} showExport={showExport} setShowExport={setShowExport}
                    showPPTModal={showPPTModal} setShowPPTModal={setShowPPTModal}
                    rfpId={rfpId} editableData={editableData}
                    isManager={isManager}
                    showVersionDetailsModal={showVersionDetailsModal}
                    setShowVersionDetailsModal={setShowVersionDetailsModal}
                    selectedVersionData={selectedVersionData} activeSection={activeSection}
                    versionsList={versionsList} setRevertTargetVersion={setRevertTargetVersion}
                    setShowRevertModal={setShowRevertModal} fetchComparison={fetchComparison}
                    handleRenameVersion={handleRenameVersion}
                    showComparisonModal={showComparisonModal} setShowComparisonModal={setShowComparisonModal}
                    comparisonData={comparisonData} hoveredTabDetails={hoveredTabDetails}
                    stripPrefix={stripPrefix} showVersionHistory={showVersionHistory}
                    setShowVersionHistory={setShowVersionHistory} activeVersionMenuId={activeVersionMenuId}
                    setActiveVersionMenuId={setActiveVersionMenuId} handleViewVersion={handleViewVersion}
                    isFetchingVersionDetails={isFetchingVersionDetails}
                    setLabelTargetVersion={setLabelTargetVersion}
                    setVersionLabelText={setVersionLabelText} setShowLabelModal={setShowLabelModal}
                    setIsSelectingCompareFromView={setIsSelectingCompareFromView}
                    isSelectingCompareFromView={isSelectingCompareFromView}
                    compareSourceVersion={compareSourceVersion} setCompareSourceVersion={setCompareSourceVersion}
                    setSelectedVersionData={setSelectedVersionData}
                    isFetchingComparison={isFetchingComparison} isGeneratingSlides={isGeneratingSlides}
                    isAnyModalOpen={isAnyModalOpen}
                />
            </div>
        );
    }

    // ── Access Denied / Error State ─────────────────────────────────────────
    if (accessError && !editableData) {
        return (
            <div className="analysis-result-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: '2rem' }}>
                <div style={{ background: '#FFFFFF', padding: '2.5rem', borderRadius: '12px', border: '1px solid #E5E7EB', maxWidth: '520px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '28px' }}>
                        🔒
                    </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>
                        Access Denied
                    </h2>
                    <p style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                        {accessError}
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                        <button
                            onClick={() => fetchRFPData()}
                            style={{ background: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB', padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: 500, cursor: 'pointer' }}
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => navigate('/proposals')}
                            style={{ background: '#FF5526', color: '#FFFFFF', border: 'none', padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: 500, cursor: 'pointer' }}
                        >
                            Back to Proposals
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Loading State ──────────────────────────────────────────────────────
    if (isInitialLoading || !editableData) {
        return <AnalysisSkeleton />;
    }

    return (
        <div className="analysis-result-page">
            {/* All workflow modals in one group */}
            <RFPModalsGroup
                setSuccessToast={setSuccessToast}
                // Key Info
                isKeyInfoModalOpen={isKeyInfoModalOpen} setIsKeyInfoModalOpen={setIsKeyInfoModalOpen}
                handleUpdateMetadata={safeHandleUpdateMetadata} isUpdating={isUpdating}
                editableData={editableData} handleMetadataChange={handleMetadataChange}
                formatForDateInput={formatForDateInput}
                // Section Assign
                isAssignModalOpen={isAssignModalOpen} setIsAssignModalOpen={setIsAssignModalOpen}
                assignments={assignments} setAssignments={setAssignments}
                rfpId={rfpId} teamMembers={teamMembers}
                fetchOverview={fetchOverview} fetchRFPData={fetchRFPData}
                // Document Info
                isDocInfoModalOpen={isDocInfoModalOpen} setIsDocInfoModalOpen={setIsDocInfoModalOpen}
                documents={documents} setDocuments={setDocuments}
                documentAssignments={documentAssignments} setDocumentAssignments={setDocumentAssignments}
                assignedUsers={assignedUsers}
                isTeamLeadViewOnly={[6, 7, 9].includes(Number(editableData?.global_status?.code))}
                // Stage Confirmation
                stageModal={stageModal} setStageModal={setStageModal}
                stageError={stageError} setStageError={setStageError}
                handleClarificationRequest={handleClarificationRequest}
                activeSection={activeSection} executeStageTransition={safeExecuteStageTransition}
                isStageUpdating={isStageUpdating}
                // AI Improvement
                showAIModal={showAIModal} setShowAIModal={setShowAIModal}
                aiPrompt={aiPrompt} setAiPrompt={setAiPrompt}
                handleAIImprove={safeHandleAIImprove} isRefining={isRefining}
                // Rename Version
                showLabelModal={showLabelModal} setShowLabelModal={setShowLabelModal}
                versionLabelText={versionLabelText} setVersionLabelText={setVersionLabelText}
                handleRenameVersion={handleRenameVersion}
                // Revert Version
                showRevertModal={showRevertModal} setShowRevertModal={setShowRevertModal}
                revertTargetVersion={revertTargetVersion} revertReason={revertReason}
                setRevertReason={setRevertReason} handleRevertVersion={handleRevertVersion}
                // Decision
                isDecisionModalOpen={isDecisionModalOpen} setIsDecisionModalOpen={setIsDecisionModalOpen}
                decisionSection={decisionSection} decisionDate={decisionDate}
                setDecisionDate={setDecisionDate} handleExtensionDecision={safeHandleExtensionDecision}
                toDMY={toDMY} isManager={isManager}
                fetchAssignedUsersData={fetchAssignedUsersData}
                // Extension Request
                isExtensionModalOpen={isExtensionModalOpen} setIsExtensionModalOpen={setIsExtensionModalOpen}
                selectedSection={selectedSection} extensionDate={extensionDate}
                setExtensionDate={setExtensionDate} extensionNotes={extensionNotes}
                setExtensionNotes={setExtensionNotes} handleRequestExtension={safeHandleRequestExtension}
            />

            <RFPAnalysisHeader
                isManager={isManager} editableData={editableData} navigate={navigate}
                setIsKeyInfoModalOpen={setIsKeyInfoModalOpen} setShowExport={setShowExport}
                rfpId={rfpId}
                fetchOverview={fetchOverview}
                executeStageTransition={executeStageTransition}
                isStageUpdating={isStageUpdating}
                isNoGo={isNoGo}
                isTeamLeadViewOnly={isTeamLeadViewOnly}
                selectedSection={selectedSection}
                handleSubmitToLead={handleSubmitToLead}
                fetchRFPData={fetchRFPData}
            />

            <style>{`
                @keyframes fadeInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            {successToast && (
                <SuccessToast toast={successToast} />
            )}

            {isTeamLeadViewOnly && (
                <div style={{
                    margin: '1rem 2rem 1rem 2rem',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    ...getViewOnlyBannerStyle()
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    <span>This proposal is in view-only mode. {getTeamLeadViewOnlyMessage()}</span>
                </div>
            )}

            <InfoCardsRow activeStep={activeStep} editableData={editableData} isManager={isManager} />

            {/* Team Member Deadline Bar: between KPI cards and Editor */}
            <TeamMemberDeadlineBar
                isManager={isManager} selectedSection={selectedSection}
                editableData={editableData} toDMY={toDMY}
                onRequestExtension={() => setIsExtensionModalOpen(true)}
                isNoGo={isNoGo}
                isJustApproved={isJustApproved}
            />

            <div className={`final-review-workspace redesigned-workspace ${!isManager ? 'member-redesigned-workspace' : ''}`}>
                <Sidebar
                    sidebarSections={sidebarSections} activeSection={activeSection}
                    setActiveSection={setActiveSection} editableData={editableData}
                    isManager={isManager} setIsAssignModalOpen={setIsAssignModalOpen}
                    setIsDocInfoModalOpen={setIsDocInfoModalOpen} assignments={assignments}
                    stripPrefix={stripPrefix} rfpId={rfpId} navigate={navigate}
                    setHoveredTabDetails={setHoveredTabDetails}
                    isSidebarCollapsed={isSidebarCollapsed} fetchOverview={fetchOverview}
                    fetchAssignedUsersData={fetchAssignedUsersData}
                    onExtensionClick={handleExtensionClick}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                />

                <ContentEditor
                    activeSection={activeSection} selectedSection={currentActiveSectionObj || selectedSection}
                    editableData={editableData} activeStep={activeStep}
                    isEditingContent={isEditingContent} setIsEditingContent={setIsEditingContent}
                    isUpdating={isUpdating}
                    handleUpdate={(customContent) => safeHandleUpdate(isEditingContent, setIsEditingContent, customContent)}
                    startEditing={startEditingSection} setShowAIModal={setShowAIModal}
                    setIsAssignModalOpen={setIsAssignModalOpen}
                    selectedCell={selectedCell} setSelectedCell={setSelectedCell}
                    localBlocks={localBlocks} setLocalBlocks={setLocalBlocks}
                    handleBlockEdit={handleBlockEdit} handleTableCellEdit={handleTableCellEdit}
                    handleSidePanelEdit={handleSidePanelEdit} handleTableAction={handleTableAction}
                    handleRenameColumn={handleRenameColumn} cancelEditing={handleCancelEditing}
                    addToHistory={addToHistory} undo={undo} redo={redo}
                    renderKeyInformation={() => (
                        <KeyInformation
                            editableData={editableData}
                            valuationDisplayVal={formatCurrency(editableData.VALUATION || editableData.valuation, editableData.CURRENCY || editableData.currency)}
                            valuationVal={editableData.VALUATION || editableData.valuation}
                        />
                    )}
                    isManager={isManager} setStageModal={setStageModal} setStageError={setStageError}
                    handleToggleSectionApproval={safeHandleToggleSectionApproval}
                    handleSubmitToLead={() => safeHandleSubmitToLead(activeSection, fetchRFPData)}
                    isSubmittingToLead={isSubmittingToLead} isApprovingSection={isApprovingSection} blockEditorRef={blockEditorRef}
                    setShowVersionHistory={setShowVersionHistory}
                    isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed}
                    assignments={assignments}
                    rfpId={rfpId}
                    fetchRFPData={fetchRFPData}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                    collabYdoc={collabYdoc}
                    collabProvider={collabProvider}
                    collabUser={collabUserMeta}
                    collabStatus={collabStatus}
                    collaborators={collaborators}
                    collabReady={collabReady}
                    collabRetry={collabRetry}
                    onLocalEdit={markLocalEdit}
                    onCancelSync={handleCancelSync}
                    isSectionReadOnly={isSectionReadOnly}
                />
            </div>

            <GlobalSubmissionActions
                activeStep={activeStep} isTeamMember={isTeamMember} isManager={isManager}
                allSectionsApproved={allSectionsApproved} isMetadataReviewed={isMetadataReviewed}
                setIsMetadataReviewed={setIsMetadataReviewed} handleStageUpdate={safeExecuteStageTransition}
                handleContinueToReview={handleGeneratePPT}
                handleFinalSubmission={() => safeExecuteStageTransition('Final Approval', 'Moving to final review.')}
                isUpdating={isUpdating} isStageUpdating={isStageUpdating}
                isNoGo={isNoGo}
                isTeamLeadViewOnly={isTeamLeadViewOnly}
                globalStatus={editableData?.global_status?.code}
                editableData={editableData}
                rfpId={rfpId}
            />

            <RFPOverlays
                updateStatus={updateStatus} alertModal={alertModal} setAlertModal={setAlertModal}
                onConfirm={handleConfirmGoNoGo}
                navigate={navigate} showExport={showExport} setShowExport={setShowExport}
                showPPTModal={showPPTModal} setShowPPTModal={setShowPPTModal}
                rfpId={rfpId} editableData={editableData}
                isManager={isManager}
                showVersionDetailsModal={showVersionDetailsModal}
                setShowVersionDetailsModal={setShowVersionDetailsModal}
                selectedVersionData={selectedVersionData} activeSection={activeSection}
                versionsList={versionsList} setRevertTargetVersion={setRevertTargetVersion}
                setShowRevertModal={setShowRevertModal} fetchComparison={fetchComparison}
                handleRenameVersion={handleRenameVersion}
                showComparisonModal={showComparisonModal} setShowComparisonModal={setShowComparisonModal}
                comparisonData={comparisonData} hoveredTabDetails={hoveredTabDetails}
                stripPrefix={stripPrefix} showVersionHistory={showVersionHistory}
                setShowVersionHistory={setShowVersionHistory} activeVersionMenuId={activeVersionMenuId}
                setActiveVersionMenuId={setActiveVersionMenuId} handleViewVersion={handleViewVersion}
                isFetchingVersionDetails={isFetchingVersionDetails}
                setLabelTargetVersion={setLabelTargetVersion}
                setVersionLabelText={setVersionLabelText} setShowLabelModal={setShowLabelModal}
                setIsSelectingCompareFromView={setIsSelectingCompareFromView}
                isSelectingCompareFromView={isSelectingCompareFromView}
                compareSourceVersion={compareSourceVersion} setCompareSourceVersion={setCompareSourceVersion}
                setSelectedVersionData={setSelectedVersionData}
                isFetchingComparison={isFetchingComparison} isGeneratingSlides={isGeneratingSlides}
                isAnyModalOpen={isAnyModalOpen}
                handleApproveVersion={handleApproveSectionVersionWithReload}
                isApprovingVersion={isApprovingVersion}
                approvingVersionNum={approvingVersionNum}
            />
        </div>
    );
};

export default RFPAnalysisResult;
